import { useState } from 'react';
import JWButton from '../brand/JWButton';
import JWModal from '../brand/JWModal';
import { Share2, Check, Twitter, Facebook, Linkedin } from 'lucide-react';
import { WhatsAppIcon, RedditIcon } from '../icons/SocialIcons';

interface CopyLinkModalProps {
    show: boolean,
    setShow: (show: boolean) => void,
    url?: string | undefined | null,
    onClose: () => void,
}

const CopyLinkModal = ({ show, setShow, url, onClose }: CopyLinkModalProps) => {
    const [copySuccess, setCopySuccess] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    const CopyClick = async () => {
        await navigator.clipboard.writeText(url || '');
        setCopySuccess(true);
        setTimeout(() => {
            setCopySuccess(false);
        }, 1500);
    };

    const shareOnSocial = (platform: string) => {
        const shareUrl = encodeURIComponent(url || '');
        const text = encodeURIComponent('Join me on JamWith - the platform for musicians to connect and collaborate!');

        let shareLink = '';
        switch (platform) {
        case 'twitter':
            shareLink = `https://twitter.com/intent/tweet?text=${text}&url=${shareUrl}`;
            break;
        case 'facebook':
            shareLink = `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`;
            break;
        case 'linkedin':
            shareLink = `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`;
            break;
        case 'whatsapp':
            shareLink = `https://wa.me/?text=${text}%20${shareUrl}`;
            break;
        case 'reddit':
            shareLink = `https://www.reddit.com/submit?url=${shareUrl}&title=${text}`;
            break;
        }

        window.open(shareLink, '_blank');
    };

    const modalBody = (
        <div className="flex flex-col w-full h-full p-4">
            <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">JamWith your friends</h2>
                <p className="text-gray-600">Bring more people to the collab party!</p>
            </div>

            <div className="flex flex-col space-y-4">
                {/* Link Input with Copy Button */}
                <div className="flex items-center space-x-2">
                    <input
                        value={url || ''}
                        readOnly={true}
                        className='flex-1 text-sm h-12 border border-gray-300 rounded-lg px-4 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent'
                    />
                    <button
                        onClick={CopyClick}
                        className="h-12 px-4 rounded-lg text-white flex items-center justify-center whitespace-nowrap bg-purple"
                        style={{
                            backgroundColor: copySuccess ? '#10B981' : '#7C3AED'
                        }}
                    >
                        {copySuccess ? (
                            <>
                                <Check className="w-4 h-4 mr-2" />
                                Copied!
                            </>
                        ) : (
                            <>
                                <Share2 className="w-4 h-4 mr-2" />
                                Copy
                            </>
                        )}
                    </button>
                </div>

                {/* Social Sharing Options */}
                <div className="flex justify-center items-center mt-6 gap-2">
                    <button
                        onClick={() => shareOnSocial('twitter')}
                        className="p-3 rounded-full bg-[#E8F5FE] hover:bg-[#D4EDFD] transition-colors"
                        title="Share on Twitter"
                    >
                        <Twitter className="w-5 h-5 text-black" />
                    </button>
                    <button
                        onClick={() => shareOnSocial('facebook')}
                        className="p-3 rounded-full bg-[#E7F3FF] hover:bg-[#D4E8FF] transition-colors"
                        title="Share on Facebook"
                    >
                        <Facebook className="w-5 h-5 text-black" />
                    </button>
                    <button
                        onClick={() => shareOnSocial('linkedin')}
                        className="p-3 rounded-full bg-[#E8F0F9] hover:bg-[#D4E4F5] transition-colors"
                        title="Share on LinkedIn"
                    >
                        <Linkedin className="w-5 h-5 text-black" />
                    </button>
                    <button
                        onClick={() => shareOnSocial('whatsapp')}
                        className="p-3 rounded-full bg-[#E8F5E9] hover:bg-[#D4E8D4] transition-colors"
                        title="Share on WhatsApp"
                    >
                        <WhatsAppIcon className="w-5 h-5 text-black" />
                    </button>
                    <button
                        onClick={() => shareOnSocial('reddit')}
                        className="p-3 rounded-full bg-[#FFE8E8] hover:bg-[#FFD4D4] transition-colors"
                        title="Share on Reddit"
                    >
                        <RedditIcon className="w-5 h-5 text-black" />
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <JWModal
            show={show}
            onClose={onClose}
            setShow={setShow}
            wrapperClassName={'md:w-[500px] w-full h-auto'}
        >
            {modalBody}
        </JWModal>
    );
};

export default CopyLinkModal;
